export class EngagementMonitor {
    constructor(browser, clock, startedAt = Date.now()) {
        this.browser = browser;
        this.clock = clock;
        this.startedAt = startedAt;
        this.maxScrollPct = 0;
        this.firstScrollAt = null;
        this.lastScrollAt = null;
        this.lastScrollY = browser.scrollY || 0;
        this.scrollVelocities = [];
        this.aboveFoldMs = 0;
        this.belowFoldMs = 0;
        this.lastFoldSampleAt = Date.now();
        this.rageScrollCount = 0;
        this.rapidScrollCount = 0;
        this.scrollDirections = [];
        this.ctaViewed = false;
        this.ctaClicked = false;
        this.sectionsViewed = new Set();
        this.sectionsTotal = 0;
        this.elementsViewed = new Set();
        this.sectionElements = [];
        this.ctaElements = [];
        this.observers = [];
        this.clickHandler = null;
        this.installed = false;
    }

    install() {
        if (this.installed) {
            return;
        }

        this.installed = true;
        this.discoverTargets();
        this.observeTargets();
        this.installClickTracking();
    }

    reset(startedAt = Date.now()) {
        this.disconnect();
        this.startedAt = startedAt;
        this.maxScrollPct = 0;
        this.firstScrollAt = null;
        this.lastScrollAt = null;
        this.lastScrollY = this.browser.scrollY || 0;
        this.scrollVelocities = [];
        this.aboveFoldMs = 0;
        this.belowFoldMs = 0;
        this.lastFoldSampleAt = startedAt;
        this.rageScrollCount = 0;
        this.rapidScrollCount = 0;
        this.scrollDirections = [];
        this.ctaViewed = false;
        this.ctaClicked = false;
        this.sectionsViewed = new Set();
        this.elementsViewed = new Set();
        this.installed = false;
        this.install();
    }

    disconnect() {
        this.observers.forEach((observer) => observer.disconnect());
        this.observers = [];

        if (this.clickHandler) {
            this.browser.document?.removeEventListener('click', this.clickHandler, true);
            this.clickHandler = null;
        }
    }

    discoverTargets() {
        const document = this.browser.document;

        if (! document?.querySelectorAll) {
            return;
        }

        const sectionSelectors = '[data-ailog-section], section[id], main section, article section';
        const sections = [...document.querySelectorAll(sectionSelectors)].slice(0, 20);
        this.sectionElements = sections.length > 0
            ? sections
            : [...document.querySelectorAll('[data-ailog-section]')];

        const ctaSelectors = '[data-ailog-cta], a[data-ailog-cta], button[data-ailog-cta]';
        this.ctaElements = [...document.querySelectorAll(ctaSelectors)].slice(0, 20);
        this.sectionsTotal = this.sectionElements.length;
    }

    observeTargets() {
        if (typeof this.browser.IntersectionObserver !== 'function') {
            return;
        }

        const sectionObserver = new this.browser.IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (! entry.isIntersecting || entry.intersectionRatio < 0.35) {
                    return;
                }

                const key = this.elementKey(entry.target);

                if (key) {
                    this.sectionsViewed.add(key);
                    this.elementsViewed.add(key);
                }
            });
        }, { threshold: [0.35, 0.5, 0.75] });

        this.sectionElements.forEach((element) => sectionObserver.observe(element));
        this.observers.push(sectionObserver);

        const ctaObserver = new this.browser.IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.ctaViewed = true;
                    const key = this.elementKey(entry.target);

                    if (key) {
                        this.elementsViewed.add(key);
                    }
                }
            });
        }, { threshold: [0.25, 0.5] });

        this.ctaElements.forEach((element) => ctaObserver.observe(element));
        this.observers.push(ctaObserver);
    }

    installClickTracking() {
        this.clickHandler = (event) => {
            const target = event?.target;

            if (! target || typeof target.closest !== 'function') {
                return;
            }

            const cta = target.closest('[data-ailog-cta], a[data-ailog-cta], button[data-ailog-cta]');

            if (cta) {
                this.ctaClicked = true;
                this.clock.markInteraction(Date.now());
            }
        };

        this.browser.document?.addEventListener('click', this.clickHandler, true);
    }

    elementKey(element) {
        if (! element) {
            return null;
        }

        return element.id
            || element.getAttribute?.('data-ailog-section')
            || element.getAttribute?.('data-ailog-cta')
            || element.tagName;
    }

    onScroll(now = Date.now()) {
        this.clock.markInteraction(now);
        this.updateScrollDepth();

        const scrollY = this.browser.scrollY || 0;

        if (this.firstScrollAt === null && scrollY > 8) {
            this.firstScrollAt = now;
        }

        const elapsed = this.lastScrollAt === null ? 0 : now - this.lastScrollAt;

        if (elapsed > 0 && elapsed <= 2000) {
            const delta = Math.abs(scrollY - this.lastScrollY);
            const velocity = Math.round((delta / elapsed) * 1000);

            if (velocity > 0) {
                this.scrollVelocities.push(velocity);

                if (this.scrollVelocities.length > 10) {
                    this.scrollVelocities.shift();
                }

                if (velocity >= 2400) {
                    this.rapidScrollCount += 1;
                }
            }

            if (delta > 0) {
                const direction = scrollY >= this.lastScrollY ? 1 : -1;
                this.scrollDirections.push({ direction, at: now });
                this.scrollDirections = this.scrollDirections.filter((entry) => now - entry.at <= 900);

                let reversals = 0;

                for (let index = 1; index < this.scrollDirections.length; index += 1) {
                    if (this.scrollDirections[index].direction !== this.scrollDirections[index - 1].direction) {
                        reversals += 1;
                    }
                }

                if (reversals >= 3) {
                    this.rageScrollCount += 1;
                    this.scrollDirections = [];
                }
            }
        }

        this.sampleFoldTime(now);
        this.lastScrollY = scrollY;
        this.lastScrollAt = now;
    }

    onInteraction(now = Date.now()) {
        this.clock.markInteraction(now);
    }

    sampleFoldTime(now = Date.now()) {
        const elapsed = now - this.lastFoldSampleAt;

        if (elapsed <= 0) {
            return;
        }

        const scrollY = this.browser.scrollY || 0;
        const viewportHeight = this.browser.innerHeight || 0;
        const belowFold = viewportHeight > 0 && scrollY > Math.max(24, viewportHeight * 0.15);

        if (belowFold) {
            this.belowFoldMs += elapsed;
        } else {
            this.aboveFoldMs += elapsed;
        }

        this.lastFoldSampleAt = now;
    }

    updateScrollDepth() {
        const body = this.browser.document?.body;
        const documentElement = this.browser.document?.documentElement;
        const totalHeight = Math.max(
            body?.scrollHeight || 0,
            documentElement?.scrollHeight || 0,
            this.browser.innerHeight || 0,
        );
        const viewportBottom = (this.browser.scrollY || 0) + (this.browser.innerHeight || 0);
        const depth = totalHeight <= 0 ? 0 : Math.min(100, Math.round((viewportBottom / totalHeight) * 100));

        this.maxScrollPct = Math.max(this.maxScrollPct, depth);
    }

    snapshot(now = Date.now()) {
        this.sampleFoldTime(now);
        this.updateScrollDepth();

        const timing = this.clock.snapshot(now);
        const averageVelocity = this.scrollVelocities.length === 0
            ? 0
            : Math.round(this.scrollVelocities.reduce((sum, value) => sum + value, 0) / this.scrollVelocities.length);

        return {
            max_scroll_pct: this.maxScrollPct,
            scroll_velocity_px_per_sec: averageVelocity,
            time_to_first_scroll_ms: this.firstScrollAt === null ? null : Math.max(0, this.firstScrollAt - this.startedAt),
            time_above_fold_seconds: Math.max(0, Math.floor(this.aboveFoldMs / 1000)),
            time_below_fold_seconds: Math.max(0, Math.floor(this.belowFoldMs / 1000)),
            sections_viewed: this.sectionsViewed.size,
            sections_total: this.sectionsTotal,
            elements_viewed: this.elementsViewed.size,
            cta_viewed: this.ctaViewed,
            cta_clicked: this.ctaClicked,
            rage_scroll_count: this.rageScrollCount,
            rapid_scroll_count: this.rapidScrollCount,
            idle_seconds: timing.idleDurationSeconds,
            active_seconds: timing.activeDurationSeconds,
            hidden_seconds: timing.hiddenDurationSeconds,
        };
    }
}
